import React from 'react';
import { TextInput, type TextInputProps } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';

const clean = (value: string): string => value.replace(/\D/g, '').slice(0, 8);

const mask = (digits: string): string => {
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export function isRealDate(value: string): boolean {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!match) return false;
    const [, day, month, year] = match.map(Number);
    if (month < 1 || month > 12 || day < 1) return false;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export const toIsoDate = (value: string): string | null => {
    if (!isRealDate(value)) return null;
    const [day, month, year] = value.split('/').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 12)).toISOString();
};

export const fromIsoDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

type Props = Omit<TextInputProps, 'value' | 'onChange'> & {
    value: string;
    onChange: (value: string) => void;
};

const FormDateInput: React.FC<Props> = ({ value, onChange, ...props }) => (
    <TextInput
        {...props}
        value={value}
        inputMode="numeric"
        autoComplete="off"
        placeholder={props.placeholder ?? 'gg/mm/aaaa'}
        leftSection={<IconCalendar size={16} stroke={1.8} />}
        onChange={(event) => onChange(mask(clean(event.currentTarget.value)))}
        onPaste={(event) => {
            event.preventDefault();
            onChange(mask(clean(event.clipboardData.getData('text'))));
        }}
    />
);

export default FormDateInput;
